import { useState } from 'react';
import { api } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, moneyInput, useList, type Column, type Row, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';

/**
 * 할부 「회사 실납부」 — 회사가 캐피탈에 실제로 내는 금액(2026-10-01 미팅 #30 · way).
 *
 * 기사 공제(할부 계약·회차)와 금액이 다를 수 있어 회계팀이 시트로 따로 관리하던 것을 옮겼다.
 * 🔴 **공제 정산월 = 납부월 − 1** — 9월 운행분 공제가 10/5 납부와 짝이다(서버 `finInstPay.ts` 주석의 실측).
 * 달마다 행을 두지 않는다 — 회차 금액은 실행일·상환월수·1회차/마지막/월납입액으로 서버가 계산한다.
 * 회계 정보라 회계·관리자(전체 범위)만 본다.
 */


const diffCell = (pay: unknown, ded: unknown, linked: boolean) => {
  if (pay == null || pay === '') return <span className="text-ink-faint">—</span>;
  if (!linked) return <span className="text-ink-faint">연결 없음</span>;
  const d = Number(pay) - Number(ded ?? 0);
  if (Math.abs(d) < 1) return <span className="text-emerald-700">같음</span>;
  return <b className={d > 0 ? 'text-red-600' : 'text-amber-700'} title="회사 납부 − 기사 공제">{d > 0 ? '+' : ''}{money(String(d))}</b>;
};

/**
 * 할부금 › 「회사 납부만(공제 없음)」 탭 — 공제 계약에 이어지지 않은 실납부(직영 · 회사할부 등).
 * 10-27 way: 실납부 전용 화면을 없애고 본 목록(`FinInst.tsx`)에 합쳤다 — 이어진 실납부는 본 목록 칸·계약 상세에서 보고,
 * 본 목록에 줄이 없는 이 계약들만 여기서 본다. 계약 추가 버튼은 본 목록 「새 계약」 옆.
 */
export function FinInstPayView({ canEdit }: { canEdit: boolean }) {
  const state = 'unlinked';
  const [corp, setCorp] = useState<string | null>(null);
  const [corps, setCorps] = useState<{ corp_label: string; n: number }[]>([]);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('fin-inst-pay', 50);
  const [nonce, setNonce] = useState(0);
  const [edit, setEdit] = useState<Row | null>(null);
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });

  const { rows, loading, error } = useList(
    () => api.listFinInstPay({ state, corp, q: q || undefined, limit: PAGE, offset: page * PAGE })
      .then((r) => { setCorps(r.corps); return r; }) as unknown as Promise<{ rows: Row[] }>,
    [state, corp, q, page, PAGE, nonce],
  );
  const payMon = (rows[0]?.pay_mon as string | undefined) ?? '';
  const dedMon = (rows[0]?.ded_mon as string | undefined) ?? '';

  const columns: Column[] = [
    { key: 'corp_label', label: '법인', phone: 'hide' },
    { key: 'pay_day', label: '결제일', align: 'right', phone: 'hide', render: (r) => (r.pay_day ? `${String(r.pay_day)}일` : '—') },
    { key: 'company', label: '대출업체' },
    { key: 'car_no', label: '차량', phone: 'pin' },
    { key: 'holder_name', label: '시트 성명', render: (r) => <span className="whitespace-pre-line">{dash(r.holder_name)}</span> },
    { key: 'owner_name', label: '공제 차주', phone: 'hide', render: (r) => dash(r.owner_name) },
    { key: 'start_date', label: '실행일', phone: 'hide' },
    { key: 'month_cnt', label: '상환', align: 'right', phone: 'hide', render: (r) => (r.month_cnt ? `${String(r.month_cnt)}개월` : '—') },
    { key: 'month_amount', label: '월납입액', align: 'right', render: (r) => money(r.month_amount) },
    { key: 'pay_amt', label: `${payMon || '이번 달'} 납부`, align: 'right', render: (r) => <b>{r.pay_amt == null ? '—' : money(r.pay_amt)}</b> },
    { key: 'ded_amt', label: `${dedMon || '지난달'} 공제`, align: 'right', render: (r) => (r.fin_inst_id ? money(r.ded_amt ?? 0) : '—') },
    { key: 'diff_amt', label: '차이', align: 'right', render: (r) => diffCell(r.pay_amt, r.ded_amt, !!r.fin_inst_id) },
    { key: 'fin_inst_id', label: '공제 계약', align: 'right', render: (r) => (r.fin_inst_id ? `#${String(r.fin_inst_id)}` : <span className="text-amber-700">연결 없음</span>) },
    { key: 'diff_reason', label: '차이 사유', render: (r) => <span className="text-xs">{dash(r.diff_reason)}</span> },
    { key: 'repay_date', label: '중도상환', phone: 'hide', render: (r) => (r.repay_date ? `${String(r.repay_date)}${r.repay_amount ? ` · ${money(r.repay_amount)}` : ''}` : r.is_closed ? '종료' : '') },
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-soft">
        기사 공제 계약에 <b>이어지지 않은</b> 회사 실납부입니다(직영 · 회사할부 등). 줄을 눌러 중도상환·공제 계약 연결을 고칩니다.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {([[null, '전체 법인'], ...corps.map((c) => [c.corp_label, `${c.corp_label} ${c.n}`] as const)] as const).map(([id, label]) => (
            <button key={String(id)} onClick={() => { setCorp(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${corp === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <input className="field w-56" placeholder="차량번호·성명·대출업체" value={term} onChange={(e) => setTerm(e.target.value)} />
      </div>
      {error && <ErrorBox message={error} />}
      {edit && (
        <Modal width="max-w-4xl">
          <PayForm row={edit} canEdit={canEdit}
                   onClose={() => setEdit(null)} onSaved={() => { setEdit(null); setNonce((n) => n + 1); }} />
        </Modal>
      )}
      <DataTable tableId="finInst.pay" columns={columns} rows={rows} loading={loading} onRowClick={(r) => setEdit(r)} />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
    </div>
  );
}

const F = (r: Row | null, k: string) => (r?.[k] == null ? '' : String(r[k]));

/** 실납부 계약 등록·수정 + 공제 계약 연결 + 차이 사유. */
export function PayForm({ row, canEdit, onClose, onSaved }: { row: Row | null; canEdit: boolean; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<Record<string, string>>(() => ({
    corpLabel: F(row, 'corp_label'), payDay: F(row, 'pay_day'), company: F(row, 'company'), carNo: F(row, 'car_no'),
    firstCarNo: F(row, 'first_car_no'), holderName: F(row, 'holder_name'), monthCnt: F(row, 'month_cnt'),
    prAmount: moneyInput(row?.pr_amount), payMethod: F(row, 'pay_method'), interest: F(row, 'interest').replace(/0+$/, '').replace(/\.$/, ''),
    startDate: F(row, 'start_date'), endDate: F(row, 'end_date'),
    firstAmount: moneyInput(row?.first_amount), lastAmount: moneyInput(row?.last_amount), monthAmount: moneyInput(row?.month_amount),
    repayDate: F(row, 'repay_date'), repayAmount: moneyInput(row?.repay_amount),
    note: F(row, 'note'), diffReason: F(row, 'diff_reason'), finInstId: F(row, 'fin_inst_id'),
  }));
  const [closed, setClosed] = useState(row?.is_closed === true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cands, setCands] = useState<Row[] | null>(null);
  const set = (k: string) => (x: string) => setV((s) => ({ ...s, [k]: x }));

  async function findCands() {
    setErr(null);
    try { setCands((await api.finInstPayCandidates({ carNo: v.carNo, startDate: v.startDate })).rows as Row[]); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api.saveFinInstPay({ ...v, finInstId: v.finInstId ? Number(v.finInstId) : null, isClosed: closed, finInstPayId: row?.fin_inst_pay_id ?? undefined });
      onSaved();
    } catch (x) { setErr(x instanceof Error ? x.message : String(x)); } finally { setBusy(false); }
  }
  async function remove() {
    if (!row || !window.confirm('이 실납부 계약을 지울까요? (기사 공제 계약·회차는 그대로입니다)')) return;
    try { await api.deleteFinInstPay({ finInstPayId: Number(row.fin_inst_pay_id) }); onSaved(); }
    catch (x) { setErr(x instanceof Error ? x.message : String(x)); }
  }

  const inp = (k: string, label: string, ph = '', cls = '') => (
    <label className="space-y-1"><span className="text-xs text-ink-soft">{label}</span>
      <input className={`field w-full ${cls}`} placeholder={ph} value={v[k]} disabled={!canEdit} onChange={(e) => set(k)(e.target.value)} /></label>
  );
  const amt = (k: string, label: string) => (
    <label className="space-y-1"><span className="text-xs text-ink-soft">{label}</span>
      <MoneyInput className="w-full" value={v[k] ?? ''} onChange={set(k)} /></label>
  );

  return (
    <form onSubmit={save} className="card space-y-3 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-bold">{row ? `회사 실납부 · ${F(row, 'car_no')}` : '회사 실납부 계약 추가'}</h2>
        {row?.source_ref ? <span className="text-xs text-ink-faint">시트 {String(row.source_ref)}</span> : null}
        <button type="button" className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>닫기</button>
      </div>
      {err && <ErrorBox message={err} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {inp('corpLabel', '법인(시트 탭)', '동영(파주)')}
        {inp('carNo', '차량번호', '경기70사1234')}
        {inp('holderName', '성명(시트 표기)')}
        {inp('company', '대출업체')}
        {inp('payDay', '결제일(일)', '5', 'text-right')}
        {inp('payMethod', '이체구분')}
        {inp('interest', '이율(%)', '5.9', 'text-right')}
        {inp('firstCarNo', '최초 차량번호·전차주')}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {amt('prAmount', '대출원금')}
        {inp('monthCnt', '상환월수', '60', 'text-right')}
        {inp('startDate', '실행일 = 첫 납부일', 'YYYY-MM-DD')}
        {inp('endDate', '만기일', 'YYYY-MM-DD')}
        {amt('firstAmount', '1회차 납부액')}
        {amt('monthAmount', '월납입액')}
        {amt('lastAmount', '마지막회차 납부액')}
        <span />
        {inp('repayDate', '중도상환일', 'YYYY-MM-DD')}
        {amt('repayAmount', '중도상환 금액')}
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input type="checkbox" checked={closed} disabled={!canEdit} onChange={(e) => setClosed(e.target.checked)} /> 할부 종료(날짜 없이 끝남)
        </label>
      </div>

      <div className="space-y-2 rounded-lg border border-line bg-surface px-3 py-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">기사 공제 계약</span>
          <input className="field w-32 text-right" placeholder="계약 번호" value={v.finInstId} disabled={!canEdit} onChange={(e) => set('finInstId')(e.target.value.replace(/[^0-9]/g, ''))} />
          {canEdit && <button type="button" className="btn-ghost py-1 text-xs" onClick={() => void findCands()}>같은 차량 계약 찾기</button>}
          {canEdit && v.finInstId && <button type="button" className="btn-ghost py-1 text-xs" onClick={() => set('finInstId')('')}>연결 끊기</button>}
          <span className="text-xs text-ink-soft">직영처럼 기사 공제가 없으면 비워 둡니다.</span>
        </div>
        {cands && (cands.length ? (
          <ul className="max-h-40 overflow-y-auto rounded border border-line text-sm">
            {cands.map((c) => (
              <li key={String(c.fin_inst_id)}>
                <button type="button" className={`w-full px-3 py-1 text-left hover:bg-canvas ${String(c.fin_inst_id) === v.finInstId ? 'font-bold' : ''}`}
                        onClick={() => set('finInstId')(String(c.fin_inst_id))}>
                  #{String(c.fin_inst_id)} · 시작 {dash(c.start_drive_mon)} · {String(c.month_cnt ?? '')}개월 · 분납 {money(c.month_amount)} · {dash(c.owner_name)}
                  {c.likely ? <span className="ml-2 rounded bg-emerald-50 px-1 text-xs text-emerald-700">시작월 맞음(첫 납부 한 달 전)</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-ink-soft">같은 차량번호의 공제 계약이 없습니다.</p>)}
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">차이 사유(공제와 금액이 다른 이유)</span>
          <input className="field w-full" value={v.diffReason} disabled={!canEdit} onChange={(e) => set('diffReason')(e.target.value)}
                 placeholder="예: 회사가 이자 일부 부담 · 승계 전 차주분 · 공제 일시 중지" />
        </label>
      </div>
      {inp('note', '비고(시트 비고)')}
      {canEdit && (
        <div className="flex gap-2">
          <button className="btn" disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
          {row && <button type="button" className="btn-ghost text-red-600" onClick={() => void remove()}>삭제</button>}
        </div>
      )}
    </form>
  );
}

/** 할부 계약 상세에 붙는 「회사 실납부」 요약 — 연결된 실납부 계약과 차이 사유. */
export function PaySummary({ pays, canEdit, onChanged }: { pays: Record<string, unknown>[]; canEdit: boolean; onChanged: () => void }) {
  const [edit, setEdit] = useState<Row | null>(null);
  if (!pays.length) {
    return (
      <p className="rounded-lg border border-dashed border-line px-3 py-2 text-xs text-ink-soft">
        연결된 회사 실납부 계약이 없습니다 — 할부금 › 「회사 납부만(공제 없음)」 탭에서 그 차량 줄을 눌러 이 계약 번호를 넣습니다.
      </p>
    );
  }
  return (
    <div className="space-y-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
      {pays.map((p) => (
        <div key={String(p.fin_inst_pay_id)} className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium">회사 실납부</span>
          <span>{dash(p.company)} · {dash(p.corp_label)} · 매월 {p.pay_day ? `${String(p.pay_day)}일` : '—'}</span>
          <span>월납입 <b>{money(p.month_amount)}</b> · 첫/마지막 {money(p.first_amount)} / {money(p.last_amount)}</span>
          <span>실행 {dash(p.start_date)} · {String(p.month_cnt ?? '')}개월{p.repay_date ? ` · 중도상환 ${String(p.repay_date)}` : ''}</span>
          <span className="text-xs">사유: {p.diff_reason ? String(p.diff_reason) : <span className="text-ink-faint">없음</span>}</span>
          <button className="btn-ghost ml-auto py-0.5 text-xs" onClick={() => setEdit(p as Row)}>{canEdit ? '수정·사유' : '보기'}</button>
        </div>
      ))}
      {edit && (
        <Modal width="max-w-4xl">
          <PayForm row={edit} canEdit={canEdit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />
        </Modal>
      )}
    </div>
  );
}
